import React, { useMemo, useCallback, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { useGetProductsQuery } from "../../api/productApi";
import {
  useGetCategoriesQuery,
  useGetTagsQuery,
  useGetBrandsQuery,
} from "../../api/productTaxonomyApi";
import {
  setCategory,
  setTag,
  setBrand,
  setPriceRange,
  setSorting,
  setPage,
  setPerPage,
  setSearch,
  setActiveView,
  setOnSale,
  setFilters,
  setPagination,
} from "../../productSlice";
import { useProductsUrlSync } from "../../hooks";
import {
  createCategoryMap,
  getParentCategories,
  getSubcategories,
  buildCategoryTree, // Import buildCategoryTree
  hasCategoryProducts, // Import hasCategoryProducts
} from "../../helpers/productsCategoryHelpers";

// Import the extracted components
import FilterSidebar from "./FilterSidebar";
import ProductsGrid from "./ProductsGrid";
import CategoriesBarLarge from "./CategoriesBarLarge"; // Import Large Bar
import CategoriesBarNarrow from "./CategoriesBarNarrow"; // Import Narrow Bar

// Import types
import type { RootState, AppDispatch } from "../../../../app/store";
import type {
  FilterState,
  PaginationState,
  OrderByOptions,
} from "../../productSlice";
import type {
  GetProductsParams,
  TransformedProduct,
} from "../../types/productApiTypes";
import type {
  TransformedWPCategory,
  TransformedWPTag,
  TransformedWPBrand,
  WPCategoriesById,
  WPTagsById,
  WPBrandsById,
} from "../../types/productTaxonomyApiTypes";

// ProductsContainer main component
const ProductsContainer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { ready: translationsReady } = useTranslation();
  const filters = useSelector((state: RootState) => state.product.filters);
  const pagination = useSelector(
    (state: RootState) => state.product.pagination
  );
  const activeView = useSelector(
    (state: RootState) => state.product.ui.activeView
  );

  // --- Offcanvas State ---
  const [isFilterOffcanvasOpen, setIsFilterOffcanvasOpen] = useState(false);

  // --- Offcanvas Handlers ---
  const openFilterOffcanvas = useCallback(() => {
    setIsFilterOffcanvasOpen(true);
  }, []);

  const closeFilterOffcanvas = useCallback(() => {
    setIsFilterOffcanvasOpen(false);
  }, []);

  const toggleFilterOffcanvas = useCallback(() => {
    setIsFilterOffcanvasOpen((prev) => !prev);
  }, []);

  // --- Effects for Offcanvas ---
  // Body scroll lock
  useEffect(() => {
    if (isFilterOffcanvasOpen) {
      document.body.classList.add("filter-offcanvas-open");
    } else {
      document.body.classList.remove("filter-offcanvas-open");
    }
    return () => {
      document.body.classList.remove("filter-offcanvas-open");
    };
  }, [isFilterOffcanvasOpen]);

  // Escape key listener
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeFilterOffcanvas();
      }
    };
    if (isFilterOffcanvasOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFilterOffcanvasOpen, closeFilterOffcanvas]);

  // --- URL Sync & Data Fetching ---
  const memoizedSetFilters = useCallback(
    (filtersData: Partial<FilterState>) => {
      dispatch(setFilters(filtersData));
    },
    [dispatch]
  );

  const memoizedSetPagination = useCallback(
    (paginationData: Partial<PaginationState>) => {
      dispatch(setPagination(paginationData));
    },
    [dispatch]
  );

  const urlSyncComplete = useProductsUrlSync(
    filters,
    pagination,
    memoizedSetFilters,
    memoizedSetPagination
  );

  const queryParams: GetProductsParams = {
    category: filters.category.length > 0 ? filters.category : undefined,
    tag: filters.tag.length > 0 ? filters.tag : undefined,
    brand: filters.brand.length > 0 ? filters.brand : undefined,
    minPrice: filters.minPrice,
    maxPrice: filters.maxPrice,
    search: filters.search || undefined,
    orderby: filters.orderby,
    onSale: filters.onSale,
    page: pagination.currentPage,
    limit: pagination.perPage,
  };

  const cleanedQueryParams = Object.entries(queryParams).reduce(
    (acc, [key, value]) => {
      if (value !== undefined) {
        acc[key as keyof GetProductsParams] = value as any;
      }
      return acc;
    },
    {} as Partial<GetProductsParams>
  );

  const {
    data: productsData,
    isLoading: isProductsLoading,
    isFetching,
    isError,
    error,
  } = useGetProductsQuery(cleanedQueryParams, {
    skip: !urlSyncComplete,
  });

  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useGetCategoriesQuery();
  const { data: tagsData, isLoading: isTagsLoading } = useGetTagsQuery();
  const { data: brandsData, isLoading: isBrandsLoading } = useGetBrandsQuery();

  const categoriesById: WPCategoriesById | undefined =
    categoriesData?.categories;
  const categoriesArray: TransformedWPCategory[] = categoriesById
    ? Object.values(categoriesById)
    : [];
  const tagsById: WPTagsById | undefined = tagsData?.tags;
  const tagsArray: TransformedWPTag[] = tagsById ? Object.values(tagsById) : [];
  const brandsById: WPBrandsById | undefined = brandsData?.brands;
  const brandsArray: TransformedWPBrand[] = brandsById
    ? Object.values(brandsById)
    : [];

  const categoryMap = useMemo(
    () => createCategoryMap(categoriesArray),
    [categoriesArray]
  );

  // Build category tree
  const categoryTree = useMemo(
    () => buildCategoryTree(categoriesArray),
    [categoriesArray]
  );

  // Filter categories with products for the bars
  const categoriesWithProductsTree = useMemo(
    () => categoryTree.filter(hasCategoryProducts),
    [categoryTree]
  );

  // --- Event Handlers ---
  const handleCategoryClick = (categoryId: number | null) => {
    if (categoryId === null) {
      dispatch(setCategory({ action: "clear" }));
      return;
    }
    // Determine if the clicked category is currently active
    const isSelected = filters.category.includes(categoryId);
    if (isSelected) {
      // If active, clear the filter (same as clicking 'All Products')
      dispatch(setCategory({ action: "clear" }));
    } else {
      // If inactive, set it as the only active category
      dispatch(setCategory({ replace: [categoryId] }));
    }
  };

  const handleTagClick = (tagId: number) => {
    dispatch(setTag(tagId));
  };
  const handleBrandClick = (brandSlug: string) => {
    dispatch(setBrand(brandSlug));
  };
  const handlePriceFilter = ({
    minPrice,
    maxPrice,
  }: {
    minPrice: number | null;
    maxPrice: number | null;
  }) => {
    dispatch(setPriceRange({ minPrice, maxPrice }));
  };
  const handleSearchFilter = (searchQuery: string) => {
    dispatch(setSearch(searchQuery));
  };
  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value as OrderByOptions;
    dispatch(setSorting(selectedValue));
  };
  const handlePageChange = (page: number) => {
    dispatch(setPage(page));
  };
  const handlePerPageChange = (perPage: number) => {
    dispatch(setPerPage(perPage));
  };
  const handleViewChange = (view: "grid" | "list") => {
    dispatch(setActiveView(view));
  };
  const handleOnSaleToggle = (checked: boolean) => {
    dispatch(setOnSale(checked));
  };

  // --- Derived Data & Loading States ---
  const products: TransformedProduct[] = productsData?.products || [];
  const paginationInfo = productsData?.pagination || {
    totalProducts: 0,
    totalPages: 0,
  };
  const paginationData = {
    currentPage: pagination.currentPage,
    perPage: pagination.perPage,
    totalPages: paginationInfo.totalPages,
    totalProducts: paginationInfo.totalProducts,
  };

  const isProductsGridLoading =
    isProductsLoading || !urlSyncComplete || !translationsReady;
  const isFilterSidebarLoading =
    !urlSyncComplete ||
    isCategoriesLoading ||
    isTagsLoading ||
    isBrandsLoading ||
    !translationsReady;

  return (
    <div className="products-container">
      {/* Offcanvas Overlay */}
      <div
        className={`filter-offcanvas-overlay ${
          isFilterOffcanvasOpen ? "is-open" : ""
        }`}
        onClick={closeFilterOffcanvas}
        aria-hidden={!isFilterOffcanvasOpen}
        tabIndex={-1}
      />

      {/* Offcanvas Filter Sidebar */}
      <div
        className={`filter-sidebar-offcanvas-wrapper ${
          isFilterOffcanvasOpen ? "is-open" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="filter-offcanvas-title"
        hidden={!isFilterOffcanvasOpen}
      >
        <FilterSidebar
          categories={categoriesArray}
          tags={tagsArray}
          brands={brandsArray}
          activeFilters={filters}
          onCategoryClick={handleCategoryClick} // Pass the simplified handler
          onTagClick={handleTagClick}
          onBrandClick={handleBrandClick}
          onPriceFilter={handlePriceFilter}
          onSearchFilter={handleSearchFilter}
          onSaleToggle={handleOnSaleToggle}
          isLoading={isFilterSidebarLoading}
          onClose={closeFilterOffcanvas}
        />
      </div>

      {/* Main Content Area */}
      <div className="products-grid-layout-main">
        {/* Category Navigation Bars */}
        <div className="category-navigation-bars">
          <CategoriesBarLarge
            categories={categoriesWithProductsTree}
            activeCategoryIds={filters.category}
            onCategoryClick={handleCategoryClick} // Pass the simplified handler
            isLoading={isCategoriesLoading}
          />
          <CategoriesBarNarrow
            categories={categoriesWithProductsTree}
            activeCategoryIds={filters.category}
            onCategoryClick={handleCategoryClick} // Pass the simplified handler
            isLoading={isCategoriesLoading}
          />
        </div>

        {/* Product Grid and Toolbar */}
        <ProductsGrid
          products={products}
          isLoading={isProductsGridLoading}
          isFetching={isFetching}
          isError={isError}
          error={error}
          pagination={paginationData}
          filters={filters}
          activeView={activeView}
          onViewChange={handleViewChange}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          onFilterToggle={toggleFilterOffcanvas}
        />
      </div>
    </div>
  );
};

export default ProductsContainer;
